/**
 * useWeatherData Hook
 *
 * Fetches weather data from the Cold Storage API for a given lat/lon.
 * Returns design day temperatures (24h) for optimization.
 */

import { useState, useEffect } from 'react';
import { apiGet, handleApiResponse, API_BASE_URL } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';

const COLD_STORAGE_API = '/api/cold_storage/v1';

export function useWeatherData(lat, lon) {
    const { currentUser } = useAuth();
    const [weatherData, setWeatherData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (lat == null || lon == null || (lat === 0 && lon === 0)) {
            setWeatherData(null);
            setError(null);
            return;
        }

        let cancelled = false;

        async function fetchWeather() {
            setLoading(true);
            setError(null);

            try {
                const url = `${API_BASE_URL}${COLD_STORAGE_API}/weather?lat=${lat}&lon=${lon}`;
                const response = await apiGet(url, currentUser);
                const data = await handleApiResponse(response);

                if (!cancelled) {
                    setWeatherData({
                        hourlyTemps: data.hourly_temps_f,
                        designDayTemps: data.design_day_temps_f,
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to fetch weather data:', err);
                    setError(err.message || 'Failed to fetch weather data');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchWeather();

        return () => {
            cancelled = true;
        };
    }, [lat, lon, currentUser]);

    return { weatherData, loading, error };
}
